import { useTransaction } from "hooks/useTransactions";
import * as i18n from "util/i18n";
import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import React, { useState } from "react";
import { bool } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { actions as thirdPaymentActions } from "reducers/thirdPayment";
import LotPaymentSummaryBody from "./components/SummaryBody";

const LotPaymentTransactionPage = ({ isDesktop }) => {
    const formatTransactionTransfer = (data, idTransaction) => ({ ...data, idTransaction });

    const {
        isLoading,
        transaction,
        dispatch,
        handleSignTransaction,
        showSignButton,
        push,
        id,
        status,
    } = useTransaction(
        `/lotPayments/newPayment/confirm`,
        thirdPaymentActions.summaryThirdPay,
        thirdPaymentActions.cleanThirdPartiesTicket,
        formatTransactionTransfer,
    );

    const { isFlowComplete, isProcessing, isFinished, isBackoffice, isFailed, isPending } = status;
    const [showMoreData, setShowMoreData] = useState(!isDesktop);
    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        "payments.newLotPayments.title",
        id,
        "lotPaymentTransactionPage",
    );

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const backLinkTo = isFlowComplete ? "/lotPayments" : "";

    const isBackofficePending = transaction?.data?.backofficePending;

    let subtitle = "";
    if (isProcessing) {
        subtitle = "forms.transaction.ticket.status.PROCESSING";
    }
    if (isPending && isBackofficePending) {
        subtitle = `ticket.status.PENDING.backoffice`;
    }

    return (
        <>
            <Notification scopeToShow="lotPaymentTransactionPage" />
            {!isBackoffice && (
                <ResponsiveHeader ticket metaTitle="payments.newLotPayments.title" backLinkTo={backLinkTo} />
            )}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader
                                title="payments.newLotPayments.title"
                                subTitle={subtitle}
                                status={isPending && isBackofficePending ? "AWAITING" : ""}
                            />
                            <div className="ticket lot-payment">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    <LotPaymentSummaryBody
                                        fromTransactionPage
                                        isDesktop={isDesktop}
                                        setShowMoreData={setShowMoreData}
                                        showMoreData={showMoreData}
                                    />

                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions no-print">
                            <Col xs={12} md={4} mdOffset={isFinished || isFailed || showSignButton ? 2 : 4}>
                                <Button
                                    onClick={() => dispatch(push("/desktop"))}
                                    label="forms.transaction.ticket.actions.backToHome"
                                    className={`btn${isFinished || showSignButton ? " btn-outline" : " btn-primary"}`}
                                />
                            </Col>
                            <Col xs={12} md={4}>
                                {isFinished || isFailed ? (
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/download.svg"
                                        label="global.download"
                                        onClick={handleDownload}
                                    />
                                ) : (
                                    <>
                                        {showSignButton && (
                                            <Button
                                                onClick={handleSignTransaction}
                                                label="forms.signTransaction.link"
                                                className="btn btn-primary"
                                            />
                                        )}
                                    </>
                                )}
                            </Col>
                            {isFinished && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </>
    );
};

LotPaymentTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default LotPaymentTransactionPage;
